import { invoke } from '@shared/easyfy.decorators';
import { globalEvent } from '@shared/easyfy.core';
import { isMobile, addCss } from '@shared/easyfy.utils';
import { replaceContent } from '@clientShared/utils';
import { GetSubscriptionFlyoutView } from '@clientWebServices/productoptions.service';
import reviewsService from '@clientWebServices/reviews.service';
import SliderHelper from '@clientShared/sliderHelper';
import MessageService from '@clientWebServices/message.service';
import { registerChangeQuantityEvents } from '@clientWebModules/changequantity';
import { OptionSelector } from '../modules/optionselector';
import SalesUnitSelector from '../modules/salesunitselector';
import '@clientWebModules/ingrid-product-widget';
import { GetOptionsSelectorFlyout } from '@clientWebServices/options.service';

const TOGGLE_OPTIONS_SELECTOR_FLYOUT = 'product-options-selector';

@invoke
export class Product {
  constructor() {
    registerChangeQuantityEvents();
  }

  onInit() {
    globalEvent({
      type: 'click',
      events: [
        {
          event: 'scroll-to-salesunit-selector',
          callback: this.scrollToSalesunitSelector,
        },
        {
          action: 'get-more-reviews',
          callback: this.loadMoreReviews,
        },
        {
          event: 'scroll-to-reviews',
          callback: this.scrollToReviews,
        },
        {
          action: TOGGLE_OPTIONS_SELECTOR_FLYOUT,
          callback: this.loadOptionsFlyout,
        },
      ],
    });

    globalEvent({
      type: 'form-success',
      events: [
        {
          event: 'change-review-button',
          callback: this.changeReviewButton,
        },
      ],
    });
    window.addEventListener('update-subs-flyout', (() => {
      this.onUpdateSubscFlyout();
    }) as EventListener);

    window.addEventListener('initialize-product-images-slider', (() => {
      this.initializeProductImagesSlider();
    }) as EventListener);

    this.initializeProductImagesSlider();
    this.selectOptionsFromHash();
  }

  private selectOptionsFromHash() {
    const pageHasVariants = !!document.querySelector('[data-has-variants]');
    const hasHash = !!window.location.hash;

    if (!hasHash) return;

    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const searchProductId = hashParams.get('v');
    const salesunitcode = hashParams.get('su');

    if (pageHasVariants && searchProductId) {
      OptionSelector.Instance.getSelectedOption(searchProductId, salesunitcode);
    } else if (salesunitcode) {
      SalesUnitSelector.Instance.selectSalesUnitBySalesUnitCode(salesunitcode);
    }
  }

  initializeProductImagesSlider = () => {
    document.querySelectorAll('[data-container="product-images-slider"]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      item.classList.add('splide');

      const slider = SliderHelper.CreateSlider(item, {
        type: 'slide',
        perPage: 1,
        arrows: false,
        lazyLoad: 'nearby',
        preloadPages: 1,
      });

      slider.on('pagination:mounted', function handlePaginationMounted(data) {
        data.list.classList.add('with-dots');
      });

      SliderHelper.MountSlider(slider);
    });
  };

  scrollToSalesunitSelector(e: Event) {
    if (!isMobile() || !(e?.target instanceof HTMLElement)) return;
    const el = e?.target;
    const elParent = el?.closest('[data-container="current-salesunit-info"]');
    if (!(elParent instanceof HTMLElement)) return;
    const hasSalesUnit = elParent?.dataset?.salesunits;
    if (hasSalesUnit && hasSalesUnit.toLowerCase() === 'true') {
      document.getElementById('salesunit-selector')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return false;
  }

  scrollToReviews() {
    const container = document.getElementById('review-container');
    const reviewDetails = container?.querySelector('details');

    reviewDetails?.setAttribute('open', '');
    container?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    return false;
  }

  onUpdateSubscFlyout() {
    const subButton = document.querySelector('[data-container="subscription-button"]');
    if (!(subButton instanceof HTMLElement)) return;
    const searchProdId = subButton?.dataset?.searchproductid;
    if (subButton && searchProdId) {
      GetSubscriptionFlyoutView(searchProdId)
        .then((res) => {
          if (res?.data?.success && res?.data?.content?.partialHtml != null) {
            const freqSelector = document.querySelector('[data-container="subscription-freq"]');
            if (freqSelector) {
              const parsedContent = document.createRange().createContextualFragment(res.data.content.partialHtml).firstElementChild;
              const updateFlyout = parsedContent?.getAttribute('data-updateflyout') ?? 'false';
              replaceContent(freqSelector, res?.data?.content?.partialHtml, 'replaceInnerHtml');
              freqSelector.setAttribute('data-updateflyout', updateFlyout);
            }
          } else {
            res?.data?.message != null ? MessageService.Instance.displayErrorMessage(res?.data?.message) : MessageService.Instance.displayDefaultErrorMessage();
          }
        })
        .catch((err) => {
          console.error(err);
          MessageService.Instance.displayDefaultErrorMessage();
        });
    }
    return false;
  }

  loadMoreReviews(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;
    const container = target?.closest('[data-container="product-reviews"]');
    const reviewsContainer = container?.querySelector('[data-container="reviews-container"]');
    const buttonContainer = container?.querySelector('[data-container="reviews-container-button"]');
    const button = container?.querySelector('[data-action="get-more-reviews"]');
    const page = button?.attributes.getNamedItem('data-page')?.value;
    const productid = button?.attributes.getNamedItem('data-product')?.value;
    const token = button?.attributes.getNamedItem('data-token')?.value;
    if (!page || !productid || !token || !reviewsContainer || !buttonContainer) return;
    reviewsService
      .ReviewListGetMore({ page: Number(page) + 1, productid: productid }, token)
      .then((response) => {
        if (response?.data?.success && response?.data?.partial != null) {
          reviewsContainer?.insertAdjacentHTML('beforeend', response.data.partial);
          buttonContainer.innerHTML = response.data.buttonpartial;
        } else {
          response?.data?.message != null
            ? MessageService.Instance.displayErrorMessage(response?.data?.message)
            : MessageService.Instance.displayDefaultErrorMessage();
        }
      })
      .catch((err) => {
        console.log(err);
        MessageService.Instance.displayDefaultErrorMessage();
      });
  }
  changeReviewButton() {
    const target = document.querySelector('[data-js-target="open-review-modal"]');
    if (!(target instanceof HTMLElement)) return;
    target.textContent = target.getAttribute('data-submitted-text') ?? '';
    target.setAttribute('disabled', 'disabled');
  }

  loadOptionsFlyout = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    const target = e.target;
    if (!(target instanceof HTMLElement)) return;

    const searchProductId = target.getAttribute('data-searchproductid');
    const attributeKey = target.getAttribute('data-attributekey');
    const isVariant = target.getAttribute('data-variant')?.toLowerCase() === 'true';
    const isProductSeries = target.getAttribute('data-productseries')?.toLowerCase() === 'true';

    if (!searchProductId || !attributeKey) return;

    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const selectedVariant = hashParams.get('v');

    GetOptionsSelectorFlyout(searchProductId, attributeKey, isVariant, selectedVariant, isProductSeries)
      .then((response) => {
        if (response?.data?.success && response?.data?.partial != null) {
          this.handleFlyoutResponse(response.data.partial, attributeKey);
        } else {
          response?.data?.message != null
            ? MessageService.Instance.displayErrorMessage(response?.data?.message)
            : MessageService.Instance.displayDefaultErrorMessage();
        }
      })
      .catch((err) => {
        console.log(err);
        MessageService.Instance.displayDefaultErrorMessage();
      });
  };

  private handleFlyoutResponse(partial: string, attributeKey: string) {
    //Remove if any flyouts are open already
    const openFlyouts = document.querySelectorAll(`[data-container="${TOGGLE_OPTIONS_SELECTOR_FLYOUT}-${attributeKey}"]`);
    if (openFlyouts) {
      openFlyouts.forEach((flyout) => {
        flyout.remove();
      });
    }

    const content = document.createRange().createContextualFragment(partial).firstElementChild;
    if (!content) return;

    document.body.appendChild(content);
    addCss(document.body, 'noof');
    addCss(document.body, 'overlay-is-active');
    content.setAttribute('data-toggle-open', '');

    const buttons: NodeListOf<HTMLButtonElement> = document.querySelectorAll(
      `[data-action="${TOGGLE_OPTIONS_SELECTOR_FLYOUT}"][data-attributekey="${attributeKey}"]`
    );

    buttons?.forEach((button) => {
      button.removeAttribute('data-action');
      button.dataset.event = 'toggle';
      button.dataset.overlay = '';
      button.dataset.target = `product-options-selector-${attributeKey}`;
    });
  }
}
